import { useCallback, useEffect, useRef, useState } from 'react';

export interface Options<T> {
  clearToken: () => void;
  getData: (token: string) => Promise<T>;
  getNewToken: () => Promise<null | string>;
  getStoredToken: () => null | string;
  onAuthFailed?: () => void;
  setToken: (token: string) => Promise<void>;
}

export interface AuthenticationState<T> {
  data: T;
  isAuthenticating: boolean;
  isTokenValid: boolean;
  token: string;
}

export default function useAuthentication<T>(opts: Options<T>): AuthenticationState<T> {
  const { clearToken, getData, getNewToken, getStoredToken, onAuthFailed, setToken } = opts;
  const [authState, setAuthState] = useState<AuthenticationState<T>>({
    data: {} as T,
    isAuthenticating: true,
    isTokenValid: false,
    token: '',
  });

  // We need a local check for the is authenticating to prevent multiple requests
  // rather than the flag in state which is passed to the children for rendering a
  // loading page, which if isn't set to begin with would allow the rest of
  // component tree to render.
  const refIsAuthenticating = useRef(false);

  const handleAuthFailed = useCallback(() => {
    if (onAuthFailed) {
      onAuthFailed();
    }

    setAuthState({
      data: {} as T,
      isAuthenticating: false,
      isTokenValid: false,
      token: '',
    });
  }, [onAuthFailed]);

  const updateState = useCallback(async () => {
    if (authState.isTokenValid) {
      try {
        const data = await getData(authState.token);
        setAuthState((state) => ({ ...state, data }));
      } catch (e) {
        handleAuthFailed();
      }
    }
  }, [authState.isTokenValid, authState.token, getData, handleAuthFailed]);

  const doAuth = useCallback(async () => {
    try {
      const token = getStoredToken() || (await getNewToken());

      if (token) {
        if (setToken) {
          await setToken(token);
        }

        const data = await getData(token);

        setAuthState({
          data,
          isAuthenticating: false,
          isTokenValid: true,
          token,
        });

        return;
      }
    } catch (e) {
      /* */
    }

    clearToken();
    handleAuthFailed();
  }, [clearToken, getData, getNewToken, getStoredToken, handleAuthFailed, setToken]);

  useEffect(() => {
    if (!refIsAuthenticating.current) {
      refIsAuthenticating.current = true;
      doAuth();
    }
  }, [doAuth]);

  useEffect(() => {
    updateState();
  }, [updateState]);

  return authState;
}
